import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { CalendarDays, Eye, Send } from "lucide-react";
import { toast } from "sonner";

const fmtLastSent = (iso) => {
  if (!iso) return "Not sent yet";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: "short", day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso.slice(0, 16); }
};

export default function WeeklyDigestTile() {
  const [meta, setMeta] = useState(null); // { last_sent_at, advisors, per_advisor_flag }
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [advisorId, setAdvisorId] = useState("");

  // Load lightweight meta once for the tile (last_sent_at, advisor list).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/weekly-digest/preview");
        setMeta(data);
      } catch { /* silent — tile still renders */ }
    })();
  }, []);

  const loadPreview = async (aid = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/weekly-digest/preview", {
        params: aid ? { advisor_id: aid } : {},
      });
      setPreview(data);
    } catch (e) {
      toast.error("Failed to load digest preview");
    } finally { setLoading(false); }
  };

  const openPreview = async () => {
    setOpen(true);
    await loadPreview(advisorId);
  };

  const sendTest = async () => {
    setSending(true);
    try {
      await api.post("/admin/weekly-digest/run-now");
      toast.success("Digest sent — check your inbox in a minute.");
      // Refresh the last-sent timestamp
      const { data } = await api.get("/admin/weekly-digest/preview");
      setMeta(data);
    } catch (e) {
      toast.error("Failed to send digest");
    } finally { setSending(false); }
  };

  const advisors = meta?.advisors || [];
  const perAdvisorOn = !!meta?.per_advisor_flag;

  return (
    <>
      <Card
        className="card-lift border-border bg-white p-6"
        data-testid="weekly-digest-tile"
      >
        <div className="mb-3 flex items-center justify-between">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-accent/10 text-accent">
            <CalendarDays className="h-4 w-4" />
          </div>
          <span
            className="text-[10px] uppercase tracking-widest text-muted-foreground"
            data-testid="weekly-digest-cron-hint"
          >
            Fires Monday 09:00 IST
          </span>
        </div>
        <div className="overline mb-1 text-muted-foreground">Weekly digest</div>
        <div className="font-dashboard text-lg font-bold text-foreground">
          Start-of-week roundup
        </div>
        <div
          className="mt-1 text-xs text-muted-foreground"
          data-testid="weekly-digest-last-sent"
        >
          Last sent: {fmtLastSent(meta?.last_sent_at)}
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            className="gap-1"
            onClick={openPreview}
            data-testid="weekly-digest-preview-btn"
          >
            <Eye className="h-3.5 w-3.5" /> Preview
          </Button>
          <Button
            size="sm"
            className="gap-1"
            onClick={sendTest}
            disabled={sending}
            data-testid="weekly-digest-send-now-btn"
          >
            <Send className="h-3.5 w-3.5" />
            {sending ? "Sending…" : "Send now"}
          </Button>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="weekly-digest-preview-dialog">
          <DialogHeader>
            <DialogTitle>This week&apos;s digest — preview</DialogTitle>
            <DialogDescription>
              {preview?.subject || "Preview of the Monday-morning roundup."}
            </DialogDescription>
          </DialogHeader>

          {/* Advisor slice picker — only useful when the flag is on and there
              are actual advisors seeded. Otherwise the current scope is
              firm-wide only, so we hide it to keep the modal clean. */}
          {advisors.length > 0 && (
            <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 p-3 text-xs">
              <span className="font-semibold text-muted-foreground">Scope:</span>
              <select
                className="rounded-md border border-border bg-white px-2 py-1 text-xs"
                value={advisorId}
                onChange={(e) => { setAdvisorId(e.target.value); loadPreview(e.target.value); }}
                data-testid="weekly-digest-advisor-select"
              >
                <option value="">Firm-wide (Rohan)</option>
                {advisors.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}&apos;s slice</option>
                ))}
              </select>
              {!perAdvisorOn && (
                <span className="ml-auto text-muted-foreground">
                  Per-advisor sends are OFF (feature flag)
                </span>
              )}
            </div>
          )}

          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded-md border border-border bg-white p-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Dropouts</div>
              <div className="font-dashboard text-xl font-bold" data-testid="weekly-digest-count-dropouts">
                {preview?.dropouts ?? "—"}
              </div>
            </div>
            <div className="rounded-md border border-border bg-white p-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Stale RA</div>
              <div className="font-dashboard text-xl font-bold" data-testid="weekly-digest-count-stale">
                {preview?.stale ?? "—"}
              </div>
            </div>
            <div className="rounded-md border border-border bg-white p-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Calls this week</div>
              <div className="font-dashboard text-xl font-bold" data-testid="weekly-digest-count-upcoming">
                {preview?.upcoming ?? "—"}
              </div>
            </div>
          </div>

          <div
            className="mt-2 h-[520px] w-full overflow-hidden rounded-md border border-border bg-white"
            data-testid="weekly-digest-preview-frame-wrap"
          >
            {loading ? (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                Loading preview…
              </div>
            ) : preview?.html ? (
              <iframe
                title="Weekly digest preview"
                srcDoc={preview.html}
                className="h-full w-full"
                sandbox=""
                data-testid="weekly-digest-preview-iframe"
              />
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                Nothing to preview yet.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
